import React from "react";
import { FillInTheText } from "@/pages/Student/Exercises/ExerciseTypes/FillInTheText";
import { SelectTheWord } from "@/pages/Student/Exercises/ExerciseTypes/SelectTheWord";
import { SpeakTheSentence } from "@/pages/Student/Exercises/ExerciseTypes/SpeakTheSentence";
import { WatchLargeVideo } from "@/pages/Student/Exercises/ExerciseTypes/WatchLargeVideo";
import { WatchSimpleVideo } from "@/pages/Student/Exercises/ExerciseTypes/WatchSimpleVideo";
import { WriteTheTranslation } from "@/pages/Student/Exercises/ExerciseTypes/WriteTheTranslation";
import { MultipleChoices } from "@/pages/Student/Exercises/ExerciseTypes/MultipleChoices";
import { DragAndDrop } from "@/pages/Student/Exercises/ExerciseTypes/DragAndDrop";
import { AudioRecording } from "@/pages/Student/Exercises/ExerciseTypes/AudioRecording";
import { ComprehensionQuestion } from "@/pages/Student/Exercises/ExerciseTypes/ComprehensionQuestion";
import { PronunciationPractice } from "@/pages/Student/Exercises/ExerciseTypes/PronunciationPractice";
import { FormattedText } from "@/pages/Student/Exercises/ExerciseTypes/FormattedText";
import { StyledSection } from "@/pages/Student/Exercises/ExerciseTypes/StyledSection";
import { Glossary } from "@/pages/Student/Exercises/ExerciseTypes/Glossary";
import { ExerciseItem } from "@/services/student/exercise.interfaces";
import {
  isAdapterType,
  type AdapterType,
} from "./registry";

export type { AdapterType };
export { ADAPTER_PLAYER_KEYS } from "./registry";

export type StudentExerciseAdapterProps = {
  item: ExerciseItem;
  moduleId: string;
};

type ExercisePlayerFactory = (item: ExerciseItem) => React.ReactNode;

type PlayerRegistry = Record<AdapterType, ExercisePlayerFactory>;

const createExerciseTypeComponents = (moduleId: string): PlayerRegistry => {
  const fillInTheText: ExercisePlayerFactory = (item) => (
    <FillInTheText item={item as never} moduleId={moduleId} />
  );
  const multipleChoice: ExercisePlayerFactory = (item) => (
    <MultipleChoices
      item={item}
      readonly={Boolean(item.answer?.isCorrect)}
      previousAnswer={
        item.answer
          ? {
              selectedOptions: item.answer.data || [],
              isCorrect: item.answer.isCorrect || false,
              correctOptions: (item.answer.isCorrect && item.answer.data) || [],
            }
          : undefined
      }
      moduleId={moduleId}
    />
  );
  const watchLargeVideo: ExercisePlayerFactory = (item) => (
    <WatchLargeVideo item={item as never} />
  );

  return {
    fill_in_the_text: fillInTheText,
    fill_in_blanks: fillInTheText,
    fill_in_the_blanks: fillInTheText,
    select_the_word: (item) => <SelectTheWord item={item as never} />,
    multiple_choice: multipleChoice,
    MultipleChoice: multipleChoice,
    speak_the_sentence: (item) => (
      <SpeakTheSentence item={item as never} moduleId={moduleId} />
    ),
    watch_large_video: watchLargeVideo,
    watch_video: watchLargeVideo,
    video: watchLargeVideo,
    watch_simple_video: (item) => <WatchSimpleVideo item={item as never} />,
    write_the_translation: (item) => (
      <WriteTheTranslation item={item as never} />
    ),
    drag_and_drop: (item) => <DragAndDrop item={item as never} />,
    audio_recording: (item) => <AudioRecording item={item as never} />,
    comprehension_question: (item) => (
      <ComprehensionQuestion item={item as never} moduleId={moduleId} />
    ),
    pronunciation_practice: (item) => (
      <PronunciationPractice item={item as never} />
    ),
    formatted_text: (item) => <FormattedText item={item} />,
    styled_section: (item) => <StyledSection item={item} />,
    glossary_display: (item) => <Glossary item={item} />,
  } satisfies PlayerRegistry;
};

export const StudentExerciseAdapter: React.FC<StudentExerciseAdapterProps> = ({
  item,
  moduleId,
}) => {
  const players = createExerciseTypeComponents(moduleId);

  if (!isAdapterType(item.type, players)) {
    console.error(`Unknown exercise type: ${item.type}`);
    return <div>Tipo de exercício desconhecido: {item.type}</div>;
  }

  return <>{players[item.type](item)}</>;
};
